import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './LegalPages.css';

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <Navbar />
      
      <div className="legal-container">
        <div className="legal-header">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: April 12, 2026</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Milan AI ("we," "our," or "us"). We operate the creator marketplace platform 
              at milan-ai.net (the "Service"). This Privacy Policy explains how we collect, use, 
              disclose, and safeguard your information when you use our Service.
            </p>
            <p>
              By accessing or using our Service, you agree to this Privacy Policy. If you do not 
              agree with the terms of this Privacy Policy, please do not access the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Personal Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, password, company name (for brands), and profile information</li>
              <li><strong>Payment Information:</strong> Bank account details for creator payments (processed securely through our payment partners)</li>
              <li><strong>Communication Data:</strong> Messages, feedback, and correspondence with our support team</li>
            </ul>

            <h3>2.2 Instagram Data (For Creators)</h3>
            <p>When you connect your Instagram Business or Creator account, we collect:</p>
            <ul>
              <li><strong>Profile Information:</strong> Username, display name, biography, profile picture, follower count, following count, and media count</li>
              <li><strong>Content Data:</strong> Your public posts, including images, captions, hashtags, likes, comments, and shares</li>
              <li><strong>Engagement Metrics:</strong> Impressions, reach, saves, shares, video views, and engagement rates</li>
              <li><strong>Audience Demographics:</strong> Aggregated data about your followers including age ranges, gender distribution, geographic locations (countries and cities), and active hours</li>
            </ul>
            <p className="highlight-box">
              <strong>Important:</strong> We only access data that you explicitly authorize through Instagram's OAuth process. 
              You can revoke access at any time through your Instagram settings or through our platform.
            </p>

            <h3>2.3 Automatically Collected Information</h3>
            <ul>
              <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent, clicks, and navigation patterns</li>
              <li><strong>Log Data:</strong> IP address, access times, and referring URLs</li>
            </ul>

            <h3>2.4 Performance Hub Data (For Brands)</h3>
            <p>
              <strong>Performance Hub</strong> is an optional subscription product that lets a brand track
              Instagram performance for a list of creators the brand already has. When you use Performance
              Hub, we additionally collect and process:
            </p>
            <ul>
              <li>
                <strong>Brand-uploaded contact lists:</strong> Instagram handles, post URLs, and email
                addresses you upload via CSV. We use these <em>only</em> to send connect invitations on your
                behalf (and resends you trigger), and to associate metrics with the right creator inside your
                workspace.
              </li>
              <li>
                <strong>Connected-creator Instagram data:</strong> For creators who accept the magic-link
                invite and connect their Instagram Business / Creator account, we collect the same scopes
                listed in Section 13 (profile basics, insights, comments) limited to the specific post URL(s)
                you uploaded for that campaign.
              </li>
              <li>
                <strong>Performance Hub access tokens:</strong> Stored encrypted at rest and used only to
                pull metrics for the connected post during the campaign&rsquo;s monitoring window.
              </li>
              <li>
                <strong>Subscription &amp; billing metadata:</strong> Razorpay order IDs, plan tier, billing
                period, and amount paid for your Performance Hub subscription.
              </li>
            </ul>
            <p className="highlight-box">
              <strong>Roles:</strong> For the contact list you upload, <strong>the brand acts as the data
              controller</strong> and confirms in our Terms (Section 6.16.3) that it has a lawful basis to
              share each creator&rsquo;s email with us. Milan AI acts as a <strong>data processor</strong>
              for that list and will not use those emails to market unrelated Milan products without the
              creator&rsquo;s independent opt-in.
            </p>
            <p>
              <strong>Performance Hub retention.</strong> Brand-uploaded rows, snapshots, and Performance Hub
              access tokens are retained for the lifetime of the Performance Hub campaign plus up to
              <strong> 30 days </strong> after the campaign is cancelled, archived, or the row is deleted.
              Connected creators can revoke our Instagram access at any time from Instagram&rsquo;s
              &ldquo;Apps and Websites&rdquo; settings; revocation immediately stops metric sync for that
              row.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            
            <h3>3.1 For Creators</h3>
            <ul>
              <li>Display your profile on our marketplace for brands to discover</li>
              <li>Calculate engagement metrics and quality scores for campaign matching</li>
              <li>Analyze audience demographics to match you with relevant brand campaigns</li>
              <li>Track campaign performance and calculate performance-based payments</li>
              <li>Provide analytics and insights about your content performance</li>
              <li>Verify content delivery and monitor sponsored post performance</li>
            </ul>

            <h3>3.2 For Brands</h3>
            <ul>
              <li>Help you discover and evaluate creators for your campaigns</li>
              <li>Provide audience demographic insights for targeting decisions</li>
              <li>Track campaign performance and ROI metrics</li>
              <li>Facilitate secure payments to creators</li>
            </ul>

            <h3>3.3 For Platform Operations</h3>
            <ul>
              <li>Maintain and improve our Service</li>
              <li>Detect and prevent fraud, abuse, and security issues</li>
              <li>Comply with legal obligations</li>
              <li>Communicate important updates and notifications</li>
              <li>Provide customer support</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Share Your Information</h2>
            
            <h3>4.1 With Other Users</h3>
            <ul>
              <li><strong>Creators:</strong> Your public profile, engagement metrics, and audience demographics are visible to brands on our marketplace</li>
              <li><strong>Brands:</strong> Your company name and campaign details are visible to creators you invite</li>
            </ul>

            <h3>4.2 With Service Providers</h3>
            <p>We share information with third-party service providers who assist us in:</p>
            <ul>
              <li>Payment processing (secure payment gateways)</li>
              <li>Cloud hosting and data storage</li>
              <li>Email communications</li>
              <li>Analytics and performance monitoring</li>
            </ul>

            <h3>4.3 Legal Requirements</h3>
            <p>We may disclose your information if required by law, regulation, or legal process.</p>

            <h3>4.4 Business Transfers</h3>
            <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</p>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage and Security</h2>
            <ul>
              <li>We store data on secure cloud servers with encryption at rest and in transit</li>
              <li>We implement industry-standard security measures including firewalls, access controls, and regular security audits</li>
              <li>Instagram access tokens are encrypted and stored securely</li>
              <li>Payment information is processed by PCI-compliant payment processors</li>
            </ul>
            <p className="highlight-box">
              While we implement strong security measures, no method of transmission over the Internet 
              is 100% secure. We cannot guarantee absolute security of your data.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Data Retention</h2>
            <ul>
              <li><strong>Account Data:</strong> Retained while your account is active, plus 30 days after deletion</li>
              <li><strong>Instagram Data:</strong> Refreshed periodically while connected; deleted within 30 days of disconnection</li>
              <li><strong>Campaign Data:</strong> Retained for 2 years for analytics and payment records</li>
              <li><strong>Payment Records:</strong> Retained for 7 years as required by financial regulations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Your Rights and Choices</h2>
            
            <h3>7.1 Access and Portability</h3>
            <p>You can access your personal data through your account dashboard or by contacting us.</p>

            <h3>7.2 Correction</h3>
            <p>You can update your profile information through your account settings.</p>

            <h3>7.3 Deletion</h3>
            <p>
              You can request deletion of your account and associated data. Visit our 
              <a href="/data-deletion"> Data Deletion page</a> for instructions.
            </p>
            <p>
              <strong>Note:</strong> If you have pending payments at the time of account deletion, 
              please review the <a href="/terms">Terms of Service</a> for payment implications 
              before proceeding.
            </p>

            <h3>7.4 Instagram Disconnection</h3>
            <p>You can disconnect your Instagram account at any time:</p>
            <ul>
              <li>Through our platform: Dashboard → Settings → Disconnect Instagram</li>
              <li>Through Instagram: Settings → Apps and Websites → Remove Milan AI</li>
            </ul>

            <h3>7.5 Marketing Communications</h3>
            <p>You can opt out of marketing emails using the unsubscribe link in our emails.</p>
          </section>

          <section className="legal-section">
            <h2>8. Children's Privacy</h2>
            <p>
              Milan AI is designed for users who are 13 years of age or older. Users below 13 
              may only access our platform with direct supervision and consent from a parent or 
              legal guardian. Should we become aware that personal data has been collected from 
              a user under 13 without proper parental authorization, we will take immediate 
              steps to delete that information and terminate the associated account.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place to protect your data in accordance with 
              this Privacy Policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Third-Party Services</h2>
            <p>Our Service integrates with third-party services including:</p>
            <ul>
              <li><strong>Instagram/Meta:</strong> For creator authentication and data access (subject to Meta's Privacy Policy)</li>
              <li><strong>Google:</strong> For authentication (subject to Google's Privacy Policy)</li>
              <li><strong>Payment Processors:</strong> For secure payment handling</li>
            </ul>
            <p>Please review the privacy policies of these third-party services.</p>
          </section>

          <section className="legal-section">
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any 
              material changes by email or through a notice on our Service. Your continued use 
              of the Service after such modifications constitutes your acceptance of the updated 
              Privacy Policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, please 
              reach out to us through our <a href="/contact">Contact Page</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Instagram Data Permissions</h2>
            <p>We request the following Instagram permissions and use them as described:</p>
            <table className="permissions-table">
              <thead>
                <tr>
                  <th>Permission</th>
                  <th>Data Accessed</th>
                  <th>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>instagram_business_basic</td>
                  <td>Profile info, follower count, posts</td>
                  <td>Display your profile on marketplace for brands to discover</td>
                </tr>
                <tr>
                  <td>instagram_business_manage_insights</td>
                  <td>Engagement metrics, audience demographics</td>
                  <td>Match with relevant campaigns, calculate performance payments</td>
                </tr>
                <tr>
                  <td>instagram_business_manage_comments</td>
                  <td>Comments on posts</td>
                  <td>Measure engagement, track campaign performance</td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
